/**
 * Detalle del proceso de onboarding de un clapper: header con datos del alta,
 * timeline vertical agrupado por etapa (pasos People-flow + pasos IT legacy),
 * link a la planilla y audit al pie. (La pausa se sacó el 21/09: nadie pausa nada, Sol.)
 */

import { currentLocale } from '@/lib/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTION_PLAIN, RESULT_PLAIN, explainError, stepTitle } from './plain-language';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  ExternalLink,
  AlertCircle,
  RotateCcw,
  Zap,
  MessageSquare,
  Mail,
  Calendar,
  Cog,
} from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import type { PeopleFlowAltaStatus, PeopleFlowAuditEntry } from '@/../../shared/types/people-flow';
import {
  PEOPLE_FLOW_STAGE_ORDER,
  isLegacyStep,
  stepLabelKey,
  type PeopleFlowStep,
  pendingText,
} from './flow-model';
import { FLOW_STATUS_DOT } from './FlowStagePipeline';

const fmt = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' });
};

const CHANNEL_ICONS = {
  slack: MessageSquare,
  mail: Mail,
  calendar: Calendar,
  internal: Cog,
} as const;

function FlowStepRow({
  step,
  idAlta,
  onChanged,
  validationMissing,
}: {
  step: PeopleFlowStep;
  idAlta: string;
  onChanged: () => void;
  /** Labels de campos faltantes — se muestran inline en validación/bloqueados. */
  validationMissing?: string[];
}) {
  const { t } = useTranslation();
  const runStep = trpc.peopleFlow.runStepNow.useMutation({
    onSuccess: () => {
      toast.success(t('users.people_panel.detail.step_executed'));
      onChanged();
    },
    onError: (e) => toast.error(e.message),
  });
  const Icon = CHANNEL_ICONS[step.channel] ?? Cog;
  const legacy = isLegacyStep(step);

  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${FLOW_STATUS_DOT[step.status]}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate font-medium">
            {legacy ? t(stepLabelKey(step)) : stepTitle(t, String(step.stepId))}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          {step.status === 'ok' && (step.executedAt ? fmt(step.executedAt) : t('users.people_panel.status.ok'))}
          {step.status === 'scheduled' && step.dueAt && (
            <>{t('users.people_panel.detail.scheduled_for')} {fmt(step.dueAt)}</>
          )}
          {step.status === 'waiting' &&
            (step.raw === 'esperando_slack' ? (
              <span className="text-yellow-700 dark:text-yellow-400">{t('users.people_panel.status.waiting_slack')}</span>
            ) : (
              t('users.people_panel.status.waiting')
            ))}
          {step.status === 'blocked_validation' && (
            <>
              {t('users.people_panel.status.blocked_validation')}
              {validationMissing && validationMissing.length > 0 && (
                <span className="text-amber-600 dark:text-amber-400"> — {validationMissing.join(' · ')}</span>
              )}
            </>
          )}
          {step.status === 'expired' && t('users.people_panel.status.expired')}
          {step.status === 'error' &&
            (String(step.stepId) === 'validation' && validationMissing && validationMissing.length > 0 ? (
              <span className="text-red-600 dark:text-red-400">
                {t('users.flowdetaildialog.faltan_en_la_planilla')} {validationMissing.join(' · ')}
              </span>
            ) : (
              <ErrorExplanation raw={step.error || step.raw} stepId={String(step.stepId)} />
            ))}
          {step.raw && step.status === 'ok' && !['enviado', 'creado', 'completa'].includes(step.raw) && (
            <span className="ml-1">· {RESULT_PLAIN(t)[step.raw] ?? step.raw}</span>
          )}
          {/* El paso corrió pero dejó algo a mano: se dice EXACTAMENTE qué
              falta. Antes esto iba en verde y nadie lo miraba. */}
          {step.status === 'ok_pending' && step.pending && (
            <span className="text-amber-700 dark:text-amber-400">
              {t('users.flowdetaildialog.falta_algo_a_mano')} {pendingText(t, step)}
            </span>
          )}
        </div>
      </div>
      {!legacy && (step.status === 'error' || step.status === 'expired') && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 shrink-0 gap-1 text-xs"
          disabled={runStep.isPending}
          onClick={() => runStep.mutate({ idAlta, stepId: String(step.stepId), mode: 'retry' })}
        >
          <RotateCcw className="h-3 w-3" />
          {t('users.people_panel.detail.retry')}
        </Button>
      )}
      {!legacy && (step.status === 'scheduled' || step.status === 'waiting') && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 shrink-0 gap-1 text-xs"
          disabled={runStep.isPending}
          onClick={() => runStep.mutate({ idAlta, stepId: String(step.stepId), mode: 'force' })}
        >
          <Zap className="h-3 w-3" />
          {t('users.people_panel.detail.send_now')}
        </Button>
      )}
    </div>
  );
}

/** Error explicado: qué pasó, qué hacer, y el crudo detrás de un toggle. */
function ErrorExplanation({ raw, stepId }: { raw?: string | null; stepId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const e = explainError(t, raw, stepId);
  return (
    <span className="block">
      <span className="text-red-600 dark:text-red-400">{e.what}</span>
      <span className="mt-0.5 block text-muted-foreground">{e.todo}</span>
      {e.technical && (
        <button
          className="mt-0.5 text-[11px] text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? t('users.flowdetaildialog.ocultar_detalle_tecnico') : t('users.flowdetaildialog.ver_detalle_tecnico')}
        </button>
      )}
      {open && e.technical && (
        <span className="mt-1 block overflow-x-auto rounded bg-muted p-1.5 font-mono text-[11px]">
          {e.technical}
        </span>
      )}
    </span>
  );
}

function AuditList({ entries }: { entries: PeopleFlowAuditEntry[] }) {
  const { t } = useTranslation();
  if (!entries.length) return null;
  return (
    <div className="mt-4 border-t pt-3">
      <h4 className="mb-2 text-sm font-semibold">{t('users.people_panel.detail.audit_title')}</h4>
      <div className="max-h-40 space-y-1 overflow-y-auto text-xs text-muted-foreground">
        {entries.map((e) => (
          <div key={e.actionId} className="flex items-baseline gap-2">
            <span className="shrink-0 tabular-nums">{fmt(e.createdAt)}</span>
            <span className="font-medium text-foreground">{ACTION_PLAIN(t)[e.action] ?? e.action}</span>
            {e.stepId && <span>· {stepTitle(t, e.stepId)}</span>}
            {e.detail && <span className="truncate">· {e.detail}</span>}
            <span className="ml-auto shrink-0">{e.performedBy}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FlowDetailDialog({
  row,
  legacySteps,
  onClose,
}: {
  row: PeopleFlowAltaStatus;
  /** Pasos IT del pipeline legacy (adapter), mergeados en la vista por etapa. */
  legacySteps: PeopleFlowStep[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const detail = trpc.peopleFlow.getFlowDetail.useQuery(
    { idAlta: row.alta.idAlta },
    { staleTime: 30_000 }
  );

  const invalidate = () => {
    utils.peopleFlow.getFlowStatus.invalidate();
    detail.refetch();
  };

  const data = detail.data ?? row;
  const allSteps = [...data.steps, ...legacySteps];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {data.alta.nombre}
            <span className="text-sm font-normal text-muted-foreground">
              {data.alta.correoWinclap || data.alta.correoPersonal} · {data.alta.comunidad} ·{' '}
              {t('users.people_panel.detail.starts')} {data.alta.fechaIngreso}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
          {data.alta.recruiterEmail && (
            <span>
              {t('users.people_panel.detail.recruiter')}: <span className="text-foreground">{data.alta.recruiterEmail}</span>
            </span>
          )}
          {data.alta.managerEmail && (
            <span>
              {t('users.people_panel.detail.manager')}: <span className="text-foreground">{data.alta.managerEmail}</span>
            </span>
          )}
          {data.alta.officeManager && (
            <span>
              {t('users.people_panel.detail.receives')}: <span className="text-foreground">{data.alta.officeManager}</span>
            </span>
          )}
        </div>

        {!data.validation.complete && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('users.people_panel.detail.missing_title')}</AlertTitle>
            <AlertDescription>{data.validation.missingLabels.join(' · ')}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" className="gap-1.5" asChild>
            <a href={data.sheetUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
              {t('users.people_panel.detail.open_sheet')}
            </a>
          </Button>
        </div>

        <div className="space-y-4">
          {PEOPLE_FLOW_STAGE_ORDER.map((stage) => {
            const inStage = allSteps.filter((s) => s.stage === stage && s.status !== 'na');
            if (!inStage.length) return null;
            const done = inStage.filter((s) => s.status === 'ok').length;
            return (
              <div key={stage}>
                <div className="mb-1 flex items-center justify-between border-b pb-1">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(`users.people_panel.stages.${stage}`)}
                  </h4>
                  <span className="text-xs text-muted-foreground">
                    {done}/{inStage.length}
                  </span>
                </div>
                {inStage.map((s) => (
                  <FlowStepRow
                    key={String(s.stepId)}
                    step={s}
                    idAlta={data.alta.idAlta}
                    onChanged={invalidate}
                    validationMissing={data.validation.missingLabels}
                  />
                ))}
              </div>
            );
          })}
        </div>

        {'audit' in data && <AuditList entries={(data as { audit: PeopleFlowAuditEntry[] }).audit} />}
      </DialogContent>
    </Dialog>
  );
}
